import { Alert, StyleSheet, View } from 'react-native'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCameraPermissions } from 'expo-camera'
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { CashierPairedLayout } from '../shared/CashierPairedLayout'
import { ScannerPanel } from './ScannerPanel'
import { ManualLookupPanel } from './ManualLookupPanel'
import { StampAwardingSheet } from '../stampAwarding/StampAwardingSheet'
import { RewardFoundSheet } from '../rewardRedemption/RewardFoundSheet'
import { useScannerIdleTimer } from './useScannerIdleTimer'
import { reportUnexpectedError } from '../../../lib/reportError'
import { isDeviceForbiddenError } from '../../../lib/deviceAuthError'
import { useVerifyQr, InvalidTokenError, TokenExpiredError, QrMemberNotFoundError, AlreadyConsumedError, NoLongerActiveError } from './useVerifyQr'
import type { CashierMemberProfile } from './useMemberSearch'
import type { CashierRedemptionScan } from './useVerifyQr'

const FLIP_DURATION = 500

export function CashierHomeScreen() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'scanner' | 'manual'>('scanner')
  const [foundMember, setFoundMember] = useState<CashierMemberProfile | null>(null)
  const [foundRedemption, setFoundRedemption] = useState<CashierRedemptionScan | null>(null)
  const [scannerActive, setScannerActive] = useState(true)
  const flipProgress = useSharedValue(0)
  const [cameraPermission] = useCameraPermissions()
  const { isIdle, resume } = useScannerIdleTimer(mode === 'scanner' && cameraPermission?.granted === true)
  const verifyQr = useVerifyQr()

  const handleQrScanned = useCallback((data: string) => {
    setScannerActive(false)

    verifyQr.mutate(data, {
      onSuccess: (result) => {
        if (result.qrType === 'member') {
          setFoundMember(result.profile)
        } else {
          setFoundRedemption(result.scan)
        }
      },
      onError: (error) => {
        // Global kick-out handler (App.tsx) already signs the device out and
        // navigates to Pairing for this — no scan error to show, and it's
        // an expected outcome, not an unexpected one worth reporting.
        if (isDeviceForbiddenError(error)) return

        let message = t('cashierHome.scanNetworkError')
        if (error instanceof TokenExpiredError) {
          message = t('cashierHome.scanExpiredError')
        } else if (error instanceof QrMemberNotFoundError) {
          message = t('cashierHome.scanMemberNotFoundError')
        } else if (error instanceof AlreadyConsumedError) {
          message = t('cashierHome.scanAlreadyConsumedError')
        } else if (error instanceof NoLongerActiveError) {
          message = t('cashierHome.scanNoLongerActiveError')
        } else if (error instanceof InvalidTokenError) {
          message = t('cashierHome.scanInvalidError')
        } else {
          reportUnexpectedError(error)
        }
        Alert.alert(
          t('cashierHome.scanErrorTitle'),
          message,
          [{ text: t('cashierHome.scanErrorDismiss'), onPress: () => setScannerActive(true) }],
        )
      },
    })
  }, [t, verifyQr])

  const flip = (to: 'scanner' | 'manual') => {
    setMode(to)
    flipProgress.value = withTiming(to === 'manual' ? 1 : 0, {
      duration: FLIP_DURATION,
      easing: Easing.inOut(Easing.ease),
    })
  }

  const frontStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 1200 },
      { rotateY: `${interpolate(flipProgress.value, [0, 1], [0, 180])}deg` },
    ],
    opacity: interpolate(flipProgress.value, [0, 0.45, 0.55, 1], [1, 1, 0, 0]),
  }))

  const backStyle = useAnimatedStyle(() => ({
    transform: [
      { perspective: 1200 },
      { rotateY: `${interpolate(flipProgress.value, [0, 1], [-180, 0])}deg` },
    ],
    opacity: interpolate(flipProgress.value, [0, 0.45, 0.55, 1], [0, 0, 1, 1]),
  }))

  return (
    <CashierPairedLayout>
      <View className="flex-1">
        <Animated.View
          style={[StyleSheet.absoluteFillObject, frontStyle]}
          pointerEvents={mode === 'scanner' ? 'auto' : 'none'}
        >
          <ScannerPanel
            isIdle={isIdle}
            scannerActive={scannerActive}
            onScanned={handleQrScanned}
            onResume={resume}
            onEnterManually={() => flip('manual')}
          />
        </Animated.View>
        <Animated.View
          style={[StyleSheet.absoluteFillObject, backStyle]}
          pointerEvents={mode === 'manual' ? 'auto' : 'none'}
        >
          <ManualLookupPanel
            onScanInstead={() => flip('scanner')}
            onMemberFound={setFoundMember}
          />
        </Animated.View>
      </View>

      <StampAwardingSheet
        member={foundMember}
        onClose={() => {
          setFoundMember(null)
          setScannerActive(true)
        }}
        onScanNext={() => {
          if (mode === 'manual') flip('scanner')
          setFoundMember(null)
          setScannerActive(true)
        }}
      />

      <RewardFoundSheet
        scan={foundRedemption}
        onClose={() => {
          setFoundRedemption(null)
          setScannerActive(true)
        }}
        onScanNext={() => {
          if (mode === 'manual') flip('scanner')
          setFoundRedemption(null)
          setScannerActive(true)
        }}
      />
    </CashierPairedLayout>
  )
}